/**
 * Workflow Validation Utilities
 *
 * Validates workflow definitions before saving to ensure they are well-formed.
 * Also provides name validation for renaming actions/triggers.
 */

import type { WorkflowDefinition, RunAfter, Action } from '@/types/workflowDefinition';
import { isIfAction, isForeachAction, isUntilAction, isSwitchAction } from '@/types/workflowDefinition';
import type { IfAction, SwitchAction } from '@/types/workflowDefinition';

/** Recursively collect all action names from a workflow definition, including nested scopes */
function collectAllActionNamesFromActions(actions: Record<string, Action>): Set<string> {
  const names = new Set<string>();
  for (const [name, action] of Object.entries(actions)) {
    names.add(name);
    if (isForeachAction(action) && action.actions) {
      collectAllActionNamesFromActions(action.actions).forEach(n => names.add(n));
    }
    if (isUntilAction(action) && action.actions) {
      collectAllActionNamesFromActions(action.actions).forEach(n => names.add(n));
    }
    if (isIfAction(action)) {
      const ifAction = action as IfAction;
      if (ifAction.actions) collectAllActionNamesFromActions(ifAction.actions).forEach(n => names.add(n));
      if (ifAction.else?.actions) collectAllActionNamesFromActions(ifAction.else.actions).forEach(n => names.add(n));
    }
    if (isSwitchAction(action)) {
      const switchAction = action as SwitchAction;
      if (switchAction.cases) {
        for (const caseData of Object.values(switchAction.cases)) {
          const typedCase = caseData as { case: string; actions?: Record<string, Action> };
          if (typedCase.actions) collectAllActionNamesFromActions(typedCase.actions).forEach(n => names.add(n));
        }
      }
      if (switchAction.default?.actions) collectAllActionNamesFromActions(switchAction.default.actions).forEach(n => names.add(n));
    }
  }
  return names;
}

// =============================================================================
// NAME VALIDATION CONSTANTS
// =============================================================================

/** Maximum length for action/trigger names */
export const MAX_NAME_LENGTH = 80;

/** Maximum length for agent tool names */
export const MAX_TOOL_NAME_LENGTH = 48;

/** Characters that are not allowed in names */
export const INVALID_NAME_CHARS = [':', '#', "'", '"'];

/** Reserved prefixes that cannot be used */
export const RESERVED_NAME_PREFIXES = ['internal.'];

/**
 * Workflow name must start with a letter, then alphanumeric with underscores/hyphens as separators.
 * Matches LogicAppsPortal validation (AddWorkflow/validation.ts).
 */
export const WORKFLOW_NAME_REGEX = /^[A-Za-z][A-Za-z0-9]*(?:[_-][A-Za-z0-9]+)*$/;

/** Reserved workflow names that cannot be used (case-insensitive) */
export const RESERVED_WORKFLOW_NAMES = ['POWERSHELL', 'WORKFLOWDISPATCHER', 'WORKFLOWNODEWORKER'];

// =============================================================================
// NAME VALIDATION TYPES
// =============================================================================

/**
 * Result of validating a node name
 */
export interface NameValidationResult {
  isValid: boolean;
  error?: string;
  normalizedName?: string;
}

// =============================================================================
// NAME VALIDATION FUNCTIONS
// =============================================================================

/**
 * Normalize a name by replacing spaces with underscores.
 * This matches LogicApps designer behavior.
 */
export function normalizeActionName(name: string): string {
  return name.trim().replace(/\s+/g, '_');
}

/**
 * Check if a name contains invalid characters
 */
function containsInvalidChars(name: string): string | null {
  for (const char of INVALID_NAME_CHARS) {
    if (name.includes(char)) {
      return char;
    }
  }
  return null;
}

/**
 * Check if a name uses a reserved prefix
 */
function usesReservedPrefix(name: string): string | null {
  const lowerName = name.toLowerCase();
  for (const prefix of RESERVED_NAME_PREFIXES) {
    if (lowerName.startsWith(prefix.toLowerCase())) {
      return prefix;
    }
  }
  return null;
}

/**
 * Validate a workflow name for creation.
 * Matches LogicAppsPortal validation rules (AddWorkflow/validation.ts).
 *
 * @param name - The proposed workflow name
 * @param existingNames - Existing workflow names (for duplicate check)
 * @returns Validation result
 */
export function validateWorkflowName(
  name: string,
  existingNames: string[] = []
): NameValidationResult {
  const trimmed = name.trim();

  if (!trimmed) {
    return { isValid: false, error: 'Workflow name cannot be empty' };
  }

  // Duplicate check (case-insensitive)
  const lowerExisting = new Set(existingNames.map((n) => n.toLowerCase()));
  if (lowerExisting.has(trimmed.toLowerCase())) {
    return { isValid: false, error: `Workflow '${trimmed}' already exists` };
  }

  // Reserved name check
  if (RESERVED_WORKFLOW_NAMES.includes(trimmed.toUpperCase())) {
    return { isValid: false, error: `'${trimmed}' is a reserved name and cannot be used` };
  }

  // Format check
  if (!WORKFLOW_NAME_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error:
        'Name must start with a letter and use only letters, numbers, dashes, or underscores. Dashes and underscores must be between characters.',
    };
  }

  return { isValid: true, normalizedName: trimmed };
}

/**
 * Validate an action name for rename operations.
 *
 * @param newName - The proposed new name
 * @param workflow - The workflow to check for duplicates
 * @param currentName - The current name (to allow keeping same name)
 * @param idReplacements - Map of pending renames to consider
 * @returns Validation result with normalized name if valid
 */
export function validateActionName(
  newName: string,
  workflow: WorkflowDefinition | null,
  currentName?: string,
  idReplacements?: Map<string, string>
): NameValidationResult {
  // Normalize the name
  const normalized = normalizeActionName(newName);

  // Empty check
  if (!normalized) {
    return { isValid: false, error: 'Name cannot be empty' };
  }

  // Length check
  if (normalized.length > MAX_NAME_LENGTH) {
    return { isValid: false, error: `Name cannot exceed ${MAX_NAME_LENGTH} characters` };
  }

  // Invalid character check
  const invalidChar = containsInvalidChars(normalized);
  if (invalidChar) {
    return { isValid: false, error: `Name cannot contain '${invalidChar}'` };
  }

  // Reserved prefix check
  const reservedPrefix = usesReservedPrefix(normalized);
  if (reservedPrefix) {
    return { isValid: false, error: `Name cannot start with reserved prefix '${reservedPrefix}'` };
  }

  // Uniqueness check - if same as current name (normalized), it's valid
  if (currentName && normalizeActionName(currentName) === normalized) {
    return { isValid: true, normalizedName: normalized };
  }

  // Check against existing actions and triggers (including nested scopes)
  if (workflow) {
    const actions = workflow.definition.actions || {};
    const triggers = workflow.definition.triggers || {};

    // Build set of all names, applying pending renames
    const existingNames = new Set<string>();

    // Collect all action names recursively (ForEach, Until, If, Switch)
    for (const name of collectAllActionNamesFromActions(actions)) {
      const effectiveName = idReplacements?.get(name) ?? name;
      existingNames.add(effectiveName.toLowerCase());
    }

    for (const name of Object.keys(triggers)) {
      const effectiveName = idReplacements?.get(name) ?? name;
      existingNames.add(effectiveName.toLowerCase());
    }

    if (existingNames.has(normalized.toLowerCase())) {
      return { isValid: false, error: `A node with name '${normalized}' already exists` };
    }
  }

  return { isValid: true, normalizedName: normalized };
}

/**
 * Validate a trigger name for rename operations.
 * Same rules as action names.
 */
export function validateTriggerName(
  newName: string,
  workflow: WorkflowDefinition | null,
  currentName?: string,
  idReplacements?: Map<string, string>
): NameValidationResult {
  // Trigger names follow the same rules as action names
  return validateActionName(newName, workflow, currentName, idReplacements);
}

/**
 * Validate a tool name for agent tools.
 * Tool names have a shorter max length.
 */
export function validateToolName(
  newName: string,
  existingNames?: string[],
  currentName?: string
): NameValidationResult {
  const normalized = normalizeActionName(newName);

  if (!normalized) {
    return { isValid: false, error: 'Name cannot be empty' };
  }

  if (normalized.length > MAX_TOOL_NAME_LENGTH) {
    return { isValid: false, error: `Tool name cannot exceed ${MAX_TOOL_NAME_LENGTH} characters` };
  }

  const invalidChar = containsInvalidChars(normalized);
  if (invalidChar) {
    return { isValid: false, error: `Name cannot contain '${invalidChar}'` };
  }

  // Check uniqueness if existing names provided
  if (existingNames && currentName !== normalized) {
    const lowerExisting = existingNames.map((n) => n.toLowerCase());
    if (lowerExisting.includes(normalized.toLowerCase())) {
      return { isValid: false, error: `A tool with name '${normalized}' already exists` };
    }
  }

  return { isValid: true, normalizedName: normalized };
}

/**
 * Agent tool inner action name pattern.
 * Must start with letter or underscore, only alphanumeric and underscores, max 48 chars.
 * Matches LogicAppsUX AGENT_CONDITION validation.
 */
const AGENT_TOOL_ACTION_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,47}$/;

/**
 * Check if a name is a template/JavaScript expression
 * Catches: @{...}, @..., ${...}, and other expression patterns
 */
function isExpression(name: string): boolean {
  // LogicApps template expressions: @{...} or @functionName(...)
  if (name.includes('@{') || name.startsWith('@')) {
    return true;
  }
  // JavaScript template literals: ${...}
  if (name.includes('${')) {
    return true;
  }
  // JavaScript expressions with common patterns
  if (name.includes('{{') || name.includes('}}')) {
    return true;
  }
  return false;
}

/**
 * Collect all action names from a workflow, including nested actions in tools.
 * Returns a Set of lowercase names for case-insensitive comparison.
 */
function collectAllActionNames(workflow: WorkflowDefinition, excludeName?: string): Set<string> {
  const names = new Set<string>();
  const actions = workflow.definition.actions || {};
  const triggers = workflow.definition.triggers || {};
  // Normalize excludeName to lowercase for case-insensitive comparison
  const excludeNameLower = excludeName?.toLowerCase();

  // Add all top-level action names
  for (const name of Object.keys(actions)) {
    const nameLower = name.toLowerCase();
    if (nameLower !== excludeNameLower) {
      names.add(nameLower);
    }
  }

  // Add all trigger names
  for (const name of Object.keys(triggers)) {
    names.add(name.toLowerCase());
  }

  // Add nested action names from agent tools
  for (const action of Object.values(actions)) {
    if (action.type === 'AgentLoop' && 'tools' in action) {
      const tools = (action as { tools?: Record<string, { actions?: Record<string, unknown> }> }).tools;
      if (tools) {
        for (const tool of Object.values(tools)) {
          if (tool.actions) {
            for (const innerName of Object.keys(tool.actions)) {
              const innerNameLower = innerName.toLowerCase();
              if (innerNameLower !== excludeNameLower) {
                names.add(innerNameLower);
              }
            }
          }
        }
      }
    }
  }

  return names;
}

/**
 * Validate a tool inner action name for agent tools.
 * Follows LogicAppsUX AGENT_CONDITION validation rules:
 * - Must start with letter or underscore
 * - Only alphanumeric characters and underscores
 * - Maximum 48 characters
 * - No duplicates across entire workflow
 * - No template expressions
 * - No invalid characters (: or #)
 *
 * @param newName - The proposed new name
 * @param workflow - The workflow to check for duplicates
 * @param currentName - The current name (to allow keeping same name)
 * @returns Validation result with normalized name if valid
 */
export function validateToolInnerActionName(
  newName: string,
  workflow: WorkflowDefinition | null,
  currentName?: string
): NameValidationResult {
  // Normalize the name (spaces to underscores)
  const normalized = normalizeActionName(newName);

  // Empty check
  if (!normalized) {
    return { isValid: false, error: 'Name cannot be empty' };
  }

  // Expression check (template or JavaScript)
  if (isExpression(normalized)) {
    return { isValid: false, error: 'Name cannot be an expression' };
  }

  // Invalid character check (: and #)
  const invalidChar = containsInvalidChars(normalized);
  if (invalidChar) {
    return { isValid: false, error: `Name cannot contain '${invalidChar}'` };
  }

  // Agent tool pattern validation (stricter than general action names)
  if (!AGENT_TOOL_ACTION_PATTERN.test(normalized)) {
    return {
      isValid: false,
      error: 'Name must start with a letter or underscore and contain only alphanumeric characters and underscores (max 48 characters)',
    };
  }

  // If same as current name (normalized), it's valid
  if (currentName && normalizeActionName(currentName) === normalized) {
    return { isValid: true, normalizedName: normalized };
  }

  // Check for duplicates across entire workflow
  if (workflow) {
    const existingNames = collectAllActionNames(workflow, currentName);
    if (existingNames.has(normalized.toLowerCase())) {
      return { isValid: false, error: `A node with name '${normalized}' already exists in the workflow` };
    }
  }

  return { isValid: true, normalizedName: normalized };
}

/**
 * Severity levels for validation messages
 */
export type ValidationSeverity = 'error' | 'warning' | 'info';

/**
 * A single validation message
 */
export interface ValidationMessage {
  severity: ValidationSeverity;
  code: string;
  message: string;
  /** Node name (action or trigger) that the message relates to */
  nodeName?: string;
  /** Node type (trigger or action) */
  nodeType?: 'trigger' | 'action';
}

/**
 * Result of validating a workflow
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationMessage[];
  warnings: ValidationMessage[];
  infos: ValidationMessage[];
}

/**
 * Create a validation message
 */
const createMessage = (
  severity: ValidationSeverity,
  code: string,
  message: string,
  nodeName?: string,
  nodeType?: 'trigger' | 'action'
): ValidationMessage => ({
  severity,
  code,
  message,
  ...(nodeName ? { nodeName } : {}),
  ...(nodeType ? { nodeType } : {}),
});

/**
 * Validate that the workflow has at least one trigger
 */
const validateHasTrigger = (workflow: WorkflowDefinition): ValidationMessage[] => {
  const triggers = workflow.definition.triggers || {};
  if (Object.keys(triggers).length === 0) {
    return [createMessage('error', 'NO_TRIGGER', 'Workflow must have at least one trigger')];
  }
  return [];
};

/**
 * Validate that all triggers have required fields
 */
const validateTriggers = (workflow: WorkflowDefinition): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];
  const triggers = workflow.definition.triggers || {};

  for (const [name, trigger] of Object.entries(triggers)) {
    // Check trigger has a type
    if (!trigger.type) {
      messages.push(
        createMessage('error', 'TRIGGER_NO_TYPE', `Trigger "${name}" is missing required "type" field`, name, 'trigger')
      );
    }
  }

  return messages;
};

/**
 * Validate that all actions have required fields and valid runAfter references
 */
const validateActions = (workflow: WorkflowDefinition): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];
  const actions = workflow.definition.actions || {};
  const triggers = workflow.definition.triggers || {};

  const validNodeNames = new Set([
    ...Object.keys(actions),
    ...Object.keys(triggers),
  ]);

  for (const [name, action] of Object.entries(actions)) {
    // Check action has a type
    if (!action.type) {
      messages.push(
        createMessage('error', 'ACTION_NO_TYPE', `Action "${name}" is missing required "type" field`, name, 'action')
      );
    }

    // Validate runAfter references
    const runAfter: RunAfter = action.runAfter || {};
    for (const dependency of Object.keys(runAfter)) {
      if (!validNodeNames.has(dependency)) {
        messages.push(
          createMessage(
            'error',
            'INVALID_RUNAFTER',
            `Action "${name}" references non-existent dependency "${dependency}"`,
            name,
            'action'
          )
        );
      }
    }
  }

  return messages;
};

/**
 * Check for orphaned actions (actions not reachable from any trigger)
 */
const validateOrphanedActions = (workflow: WorkflowDefinition): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];
  const actions = workflow.definition.actions || {};
  const triggers = workflow.definition.triggers || {};

  if (Object.keys(triggers).length === 0) {
    // No triggers, can't validate connectivity
    return [];
  }

  // Build a graph of which actions depend on which nodes
  const dependentsMap = new Map<string, Set<string>>();

  // Initialize with trigger names
  for (const triggerName of Object.keys(triggers)) {
    dependentsMap.set(triggerName, new Set());
  }

  // Initialize with action names
  for (const actionName of Object.keys(actions)) {
    dependentsMap.set(actionName, new Set());
  }

  // Build reverse dependency map (what depends on each node)
  for (const [actionName, action] of Object.entries(actions)) {
    const runAfter: RunAfter = action.runAfter || {};
    for (const dependency of Object.keys(runAfter)) {
      const dependents = dependentsMap.get(dependency);
      if (dependents) {
        dependents.add(actionName);
      }
    }
  }

  // BFS from triggers to find all reachable actions
  const reachable = new Set<string>();
  const queue: string[] = [...Object.keys(triggers)];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);

    const dependents = dependentsMap.get(current);
    if (dependents) {
      for (const dependent of dependents) {
        if (!reachable.has(dependent)) {
          queue.push(dependent);
        }
      }
    }
  }

  // Find orphaned actions (those not reachable from triggers)
  for (const actionName of Object.keys(actions)) {
    if (!reachable.has(actionName)) {
      // An action is "orphaned" if it has no runAfter dependencies (not connected to anything)
      // or if its runAfter only references other orphaned actions
      const runAfter: RunAfter = actions[actionName]?.runAfter || {};
      const dependencies = Object.keys(runAfter);

      if (dependencies.length === 0) {
        // Action with no runAfter that's not connected to trigger flow
        messages.push(
          createMessage(
            'warning',
            'ORPHANED_ACTION',
            `Action "${actionName}" has no dependencies and is not connected to any trigger`,
            actionName,
            'action'
          )
        );
      }
    }
  }

  return messages;
};

/**
 * Check for circular dependencies
 */
const validateNoCircularDependencies = (workflow: WorkflowDefinition): ValidationMessage[] => {
  const messages: ValidationMessage[] = [];
  const actions = workflow.definition.actions || {};

  // Build dependency graph
  const graph = new Map<string, string[]>();
  for (const [name, action] of Object.entries(actions)) {
    const runAfter: RunAfter = action.runAfter || {};
    graph.set(name, Object.keys(runAfter));
  }

  // DFS to detect cycles
  const visited = new Set<string>();
  const inStack = new Set<string>();

  const hasCycle = (node: string, path: string[]): string[] | null => {
    if (inStack.has(node)) {
      // Found cycle - return the path
      const cycleStart = path.indexOf(node);
      return path.slice(cycleStart);
    }

    if (visited.has(node)) {
      return null;
    }

    visited.add(node);
    inStack.add(node);

    const dependencies = graph.get(node) || [];
    for (const dep of dependencies) {
      const cycle = hasCycle(dep, [...path, node]);
      if (cycle) {
        return cycle;
      }
    }

    inStack.delete(node);
    return null;
  };

  for (const actionName of graph.keys()) {
    const cycle = hasCycle(actionName, []);
    if (cycle) {
      messages.push(
        createMessage(
          'error',
          'CIRCULAR_DEPENDENCY',
          `Circular dependency detected: ${cycle.join(' -> ')} -> ${cycle[0]}`,
          actionName,
          'action'
        )
      );
      break; // Only report one cycle to avoid duplicates
    }
  }

  return messages;
};

/**
 * Validate a workflow definition
 */
export const validateWorkflow = (workflow: WorkflowDefinition | null): ValidationResult => {
  if (!workflow) {
    return {
      isValid: false,
      errors: [createMessage('error', 'NO_WORKFLOW', 'No workflow to validate')],
      warnings: [],
      infos: [],
    };
  }

  const allMessages: ValidationMessage[] = [
    ...validateHasTrigger(workflow),
    ...validateTriggers(workflow),
    ...validateActions(workflow),
    ...validateOrphanedActions(workflow),
    ...validateNoCircularDependencies(workflow),
  ];

  const errors = allMessages.filter((m) => m.severity === 'error');
  const warnings = allMessages.filter((m) => m.severity === 'warning');
  const infos = allMessages.filter((m) => m.severity === 'info');

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    infos,
  };
};

/**
 * Check if a workflow is valid (quick check without full messages)
 */
export const isWorkflowValid = (workflow: WorkflowDefinition | null): boolean => {
  return validateWorkflow(workflow).isValid;
};

/**
 * Get validation error count
 */
export const getValidationErrorCount = (workflow: WorkflowDefinition | null): number => {
  return validateWorkflow(workflow).errors.length;
};

/**
 * Get validation warning count
 */
export const getValidationWarningCount = (workflow: WorkflowDefinition | null): number => {
  return validateWorkflow(workflow).warnings.length;
};

/**
 * A workflow is "empty" when its definition has no triggers AND no actions.
 * The publish flow blocks promoting such workflows to production (#1374):
 * see `usePublishDraft` and `PublishButton`.
 */
export function isWorkflowEmpty(workflow: WorkflowDefinition | null | undefined): boolean {
  if (!workflow?.definition) return false;
  const triggerCount = Object.keys(workflow.definition.triggers ?? {}).length;
  const actionCount = Object.keys(workflow.definition.actions ?? {}).length;
  return triggerCount === 0 && actionCount === 0;
}
